import { Injectable, NgZone, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';

@Injectable({
	providedIn: 'root',
})
export class ConnectivityService {
	private readonly destroyRef = inject(DestroyRef);
	private readonly offlineSubject = new BehaviorSubject<boolean>(!navigator.onLine);
	private readonly apiUnreachableSubject = new BehaviorSubject<boolean>(false);
	private readonly pollIntervalMs = 10000; // 10s
	private readonly healthUrl = this.getHealthUrl();
	private onlineListener?: () => void;
	private offlineListener?: () => void;

	readonly isOffline$: Observable<boolean> = this.offlineSubject.asObservable();
	readonly isApiUnreachable$: Observable<boolean> = this.apiUnreachableSubject.asObservable();

	constructor(private zone: NgZone, private http: HttpClient) {
		// Listen to browser online/offline events and update within Angular zone
		this.onlineListener = () => {
			this.zone.run(() => this.offlineSubject.next(false));
		};
		this.offlineListener = () => {
			this.zone.run(() => this.offlineSubject.next(true));
		};

		window.addEventListener('online', this.onlineListener);
		window.addEventListener('offline', this.offlineListener);

		// Clean up event listeners on destroy
		this.destroyRef.onDestroy(() => {
			if (this.onlineListener) {
				window.removeEventListener('online', this.onlineListener);
			}
			if (this.offlineListener) {
				window.removeEventListener('offline', this.offlineListener);
			}
		});

		// Begin polling the API health endpoint
		this.startPolling();
	}

	setApiUnreachable(unreachable: boolean): void {
		this.apiUnreachableSubject.next(unreachable);
	}

	private startPolling(): void {
		// Poll on an interval; set unreachable=true on error, false on success
		// Use takeUntilDestroyed to automatically clean up subscription
		timer(0, this.pollIntervalMs)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				// If the browser is offline, don't attempt the request; flags are already handled
				if (!navigator.onLine) {
					return;
				}

				this.http
					.get(this.healthUrl, { responseType: 'text' })
					.pipe(
						catchError(() => {
							// Any error implies we can't reach the API right now
							this.setApiUnreachable(true);
							// Swallow error for polling
							return [];
						}),
					)
					.subscribe({
						next: () => {
							// Recovered connectivity
							this.setApiUnreachable(false);
						},
					});
			});
	}

	private getHealthUrl(): string {
		// environment.apiUrl typically ends with /api; health is usually at the root
		const base = environment.apiUrl.replace(/\/api\/?$/i, '');
		return `${base}/health`;
	}
}


